import React from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Footer from '../../components/Footer';

const EndoReferralLandingPage = () => {
  const navigate = useNavigate();

  const handleLogin = () => {
    navigate('/login');
  };

  return (
    <>
      <Helmet>
        <title>EndoReferral - Système de Référencement Endodontique</title>
        <meta name="description" content="Plateforme professionnelle de gestion des adressages endodontiques pour dentistes et endodontistes" />
      </Helmet>
      
      <div className="min-h-screen bg-[#1A1A1E] text-white">
        {/* Hero Section */}
        <section className="relative overflow-hidden">
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-[#1A1A1E] to-[#1A1A1E]" />
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
            <div className="text-center max-w-4xl mx-auto">
              {/* Logo */}
              <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-blue-500 to-blue-600 rounded-3xl mb-8 shadow-2xl">
                <Icon name="Activity" size={48} className="text-white" />
              </div>
              
              {/* Main Heading */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-white via-blue-100 to-white bg-clip-text text-transparent">
                EndoReferral
              </h1>
              
              <p className="text-xl sm:text-2xl text-gray-300 mb-4 font-light">
                Système professionnel de gestion des adressages endodontiques
              </p>
              
              <p className="text-lg text-gray-400 mb-12 max-w-2xl mx-auto leading-relaxed">
                Simplifiez la communication entre dentistes généralistes et endodontistes spécialisés. 
                Gérez vos référencements de manière sécurisée et professionnelle.
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button
                  onClick={handleLogin}
                  className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl font-semibold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 flex items-center justify-center gap-3"
                >
                  <Icon name="LogIn" size={24} />
                  <span>Connexion</span>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-20 bg-gradient-to-b from-[#1A1A1E] to-[#0F0F12]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Comment fonctionne le système ?</h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                Un processus simple et sécurisé pour gérer vos adressages endodontiques
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {/* Step 1 */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-300">
                <div className="w-16 h-16 bg-blue-600/20 rounded-xl flex items-center justify-center mb-6">
                  <Icon name="Link" size={32} className="text-blue-400" />
                </div>
                <h3 className="text-xl font-semibold mb-3">1. Lien personnalisé</h3>
                <p className="text-gray-400 leading-relaxed">
                  Chaque endodontiste dispose d'un lien unique <span className="text-blue-400 font-mono">/p/:slug</span> à partager avec ses confrères dentistes généralistes.
                </p>
              </div>
              
              {/* Step 2 */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-300">
                <div className="w-16 h-16 bg-blue-600/20 rounded-xl flex items-center justify-center mb-6">
                  <Icon name="FileText" size={32} className="text-blue-400" />
                </div>
                <h3 className="text-xl font-semibold mb-3">2. Formulaire d'adressage</h3>
                <p className="text-gray-400 leading-relaxed">
                  Les dentistes remplissent un formulaire complet avec les informations du patient, motif de consultation, et documents cliniques.
                </p>
              </div>
              
              {/* Step 3 */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-300">
                <div className="w-16 h-16 bg-blue-600/20 rounded-xl flex items-center justify-center mb-6">
                  <Icon name="Download" size={32} className="text-blue-400" />
                </div>
                <h3 className="text-xl font-semibold mb-3">3. Génération PDF automatique</h3>
                <p className="text-gray-400 leading-relaxed">
                  Le système génère automatiquement un PDF professionnel avec le branding de l'endodontiste et toutes les informations du patient.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-[#0F0F12]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Fonctionnalités clés</h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                Tout ce dont vous avez besoin pour gérer efficacement vos référencements
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Feature 1 */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:border-blue-500/50 transition-all duration-300">
                <Icon name="Lock" size={28} className="text-blue-400 mb-4" />
                <h3 className="text-lg font-semibold mb-2">Sécurité des données</h3>
                <p className="text-gray-400 text-sm">
                  Conformité RGPD complète. Les données patients sont traitées de manière sécurisée et confidentielle.
                </p>
              </div>
              
              {/* Feature 2 */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:border-blue-500/50 transition-all duration-300">
                <Icon name="Palette" size={28} className="text-blue-400 mb-4" />
                <h3 className="text-lg font-semibold mb-2">Branding personnalisé</h3>
                <p className="text-gray-400 text-sm">
                  Chaque endodontiste peut personnaliser son interface avec son logo, couleurs et informations de cabinet.
                </p>
              </div>
              
              {/* Feature 3 */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:border-blue-500/50 transition-all duration-300">
                <Icon name="Zap" size={28} className="text-blue-400 mb-4" />
                <h3 className="text-lg font-semibold mb-2">Workflow optimisé</h3>
                <p className="text-gray-400 text-sm">
                  Processus simplifié de la soumission à la génération PDF, sans friction pour les dentistes référents.
                </p>
              </div>
              
              {/* Feature 4 */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:border-blue-500/50 transition-all duration-300">
                <Icon name="BarChart3" size={28} className="text-blue-400 mb-4" />
                <h3 className="text-lg font-semibold mb-2">Tableau de bord analytique</h3>
                <p className="text-gray-400 text-sm">
                  Suivez vos référencements, statistiques et performances en temps réel depuis votre espace praticien.
                </p>
              </div>
              
              {/* Feature 5 */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:border-blue-500/50 transition-all duration-300">
                <Icon name="MessageSquare" size={28} className="text-blue-400 mb-4" />
                <h3 className="text-lg font-semibold mb-2">Communication facilitée</h3>
                <p className="text-gray-400 text-sm">
                  Échangez facilement avec vos confrères grâce aux informations de contact intégrées dans chaque référencement.
                </p>
              </div>
              
              {/* Feature 6 */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:border-blue-500/50 transition-all duration-300">
                <Icon name="FileCheck" size={28} className="text-blue-400 mb-4" />
                <h3 className="text-lg font-semibold mb-2">Gestion des documents</h3>
                <p className="text-gray-400 text-sm">
                  Téléchargez et organisez les radiographies, photos cliniques et documents associés à chaque cas.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gradient-to-b from-[#0F0F12] to-[#1A1A1E]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold mb-6">Pourquoi choisir EndoReferral ?</h2>
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-600/20 rounded-lg flex items-center justify-center">
                      <Icon name="CheckCircle2" size={24} className="text-blue-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2">Interface professionnelle</h3>
                      <p className="text-gray-400">
                        Design médical moderne et épuré qui inspire confiance auprès de vos confrères.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-600/20 rounded-lg flex items-center justify-center">
                      <Icon name="CheckCircle2" size={24} className="text-blue-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2">Conformité réglementaire</h3>
                      <p className="text-gray-400">
                        Respect total du RGPD et des normes de sécurité des données de santé.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-600/20 rounded-lg flex items-center justify-center">
                      <Icon name="CheckCircle2" size={24} className="text-blue-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2">Gain de temps considérable</h3>
                      <p className="text-gray-400">
                        Automatisation complète du processus d'adressage, de la saisie à la génération du document final.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
                <div className="space-y-6">
                  <div className="flex items-center gap-4 pb-6 border-b border-white/10">
                    <Icon name="Users" size={32} className="text-blue-400" />
                    <div>
                      <p className="text-2xl font-bold">Multi-praticiens</p>
                      <p className="text-gray-400 text-sm">Gestion centralisée pour plusieurs endodontistes</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 pb-6 border-b border-white/10">
                    <Icon name="Globe" size={32} className="text-blue-400" />
                    <div>
                      <p className="text-2xl font-bold">Accessible partout</p>
                      <p className="text-gray-400 text-sm">Plateforme web accessible depuis n'importe quel appareil</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <Icon name="Headphones" size={32} className="text-blue-400" />
                    <div>
                      <p className="text-2xl font-bold">Support dédié</p>
                      <p className="text-gray-400 text-sm">Assistance technique et accompagnement personnalisé</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-[#1A1A1E]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Prêt à simplifier vos adressages ?</h2>
            <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
              Connectez-vous dès maintenant pour accéder à votre espace et commencer à gérer vos référencements de manière professionnelle.
            </p>
            
            <div className="mt-12 pt-8 border-t border-white/10">
              <div className="flex items-center justify-center gap-2 text-sm text-gray-400">
                <Icon name="Shield" size={16} className="text-green-500" />
                <span>Plateforme sécurisée - Données médicales protégées - Conformité RGPD</span>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <Footer />
      </div>
    </>
  );
};

export default EndoReferralLandingPage;